import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import i18n from 'live/services/i18n'
import CardContainer, { ControlButton } from 'live/components/ui/CardContainer'
import styled from 'styled-components'
import { NoResults } from 'live/components/ui/SearchBar'
import { getAnalysisByActionPlanStatus, getAnalysisWithFieldsThatMatchTheFilter } from './sensorialTabUtils'
import { AnalysisFilters } from './analysisFilters'
import { AnalysisListTable } from './analysisListTable'

interface IProps {
    selectedSpan: string
    setSelectedSpan: Dispatch<SetStateAction<string>>
    analysisResults: AnalysisEvent[]
    setSelectedAnalysis: Dispatch<SetStateAction<AnalysisEvent>>
    setSelectedStoppedLine: Dispatch<SetStateAction<StoppedLineEvent>>
    isLoadingResults: boolean
}
export function QualityAnalysisList({
    analysisResults,
    setSelectedAnalysis,
    setSelectedStoppedLine,
    selectedSpan,
    setSelectedSpan,
    isLoadingResults
}: IProps): JSX.Element {
    const [searchFilter, setSearchFilter] = useState('')
    const [filteredAnalysis, setFilteredAnalysis] = useState(analysisResults)
    const [actionPlansView, setActionPlansView] = useState<ActionPlanStatusFilter>('all')
    const [isCardOpen, setIsCardOpen] = useState<boolean>(true)

    useEffect(() => {
        const analysisWithSelectedStatus = getAnalysisByActionPlanStatus(analysisResults, actionPlansView)
        const analysisThatMatchTheFilter = getAnalysisWithFieldsThatMatchTheFilter(
            searchFilter,
            analysisWithSelectedStatus
        )
        setFilteredAnalysis(analysisThatMatchTheFilter)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchFilter, analysisResults, actionPlansView])

    return (
        <StyledAnalysisList className={`analysisList ${isCardOpen ? 'cardOpen' : ''}`}>
            <CardContainer
                title={i18n('Recent Analysis')}
                collapsed={false}
                subtitle={''}
                stickyControls={false}
                renderControls={controlParams => collapsedRenderControls(controlParams, setIsCardOpen)}
                className={'analysisList--cardContainer'}
            >
                <AnalysisFilters
                    searchFilter={searchFilter}
                    setSearchFilter={setSearchFilter}
                    actionPlansView={actionPlansView}
                    setActionPlansView={setActionPlansView}
                    selectedSpan={selectedSpan}
                    setSelectedSpan={setSelectedSpan}
                />
                {isLoadingResults ? (
                    <SpinnerContainer>
                        <i className="far fa-spin fa-spinner" />
                    </SpinnerContainer>
                ) : null}
                {!filteredAnalysis.length && searchFilter && !isLoadingResults ? (
                    <NoResults show={true} searchLoading={false} clearFilters={() => setSearchFilter('')} />
                ) : null}
                {!filteredAnalysis.length && !searchFilter && !isLoadingResults ? (
                    <div className="analysisList--cardContainer__emptyMessage">
                        <p className="analysisList--cardContainer__emptyMessage--icon">
                            <i className="far fa-empty-set"></i>
                        </p>
                        <p className="analysisList--cardContainer__emptyMessage--header">
                            {i18n(`No Registered analysis for selected span`)}
                        </p>
                        <p className="analysisList--cardContainer__emptyMessage--message">
                            {i18n(`To view analysis results, please change the selected span`)}
                        </p>
                    </div>
                ) : null}
                {filteredAnalysis.length ? (
                    <AnalysisListTable
                        filteredAnalysis={filteredAnalysis}
                        setSelectedAnalysis={setSelectedAnalysis}
                        setSelectedStoppedLine={setSelectedStoppedLine}
                    />
                ) : null}
            </CardContainer>
        </StyledAnalysisList>
    )
}
const SpinnerContainer = styled.div`
    font-size: 40px;
    display: flex;
    justify-content: center;
    margin: 68px 0;
    align-items: center;
`

const StyledAnalysisList = styled.div`
    &.cardOpen {
        max-height: 70%;
        height: 70%;
        min-height: 500px;
    }
    transition: all 0.5s ease-in-out;
    max-height: 55px;
    overflow: hidden;
    margin-bottom: 35px;
    .analysisList {
        &--cardContainer {
            padding: 0;
            display: flex;
            flex-direction: column;
            height: 100%;
            > div:last-child {
                height: 100%;
                width: 100%;
            }
            &__emptyMessage {
                font-family: var(--font-semibold);
                margin: 8% 0;
                text-align: center;
                height: 100%;
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                &--icon {
                    color: var(--gray-500);
                    font-size: 2rem;
                    margin: 0;
                }
                &--header {
                    color: var(--gray-800);
                    margin: 0;
                    font-weight: 900;
                }
                &--message {
                    color: var(--gray-500);
                    font-style: italic;
                }
            }
        }
    }
    header {
        padding: 16px 20px;
    }
`

interface IRenderControls {
    collapsed: boolean
    toggleCollapsed: (boolean) => void
}
const collapsedRenderControls = (
    { collapsed, toggleCollapsed }: IRenderControls,
    setIsOpen: Dispatch<SetStateAction<boolean>>
): React.ReactNode => {
    setIsOpen(!collapsed)

    return (
        <ControlButton>
            <i onClick={() => toggleCollapsed(!collapsed)} className={`far fa-chevron-${collapsed ? 'down' : 'up'}`} />
        </ControlButton>
    )
}
